import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { fetchAuthSession } from "aws-amplify/auth"
import {
  GatedAutoPaySelectCardFailReason,
  PromoFailReason,
} from "ipctg-inflow-types/InFlowEnums"
import {
  GatedAutoPaySelectCardPOSTSuccessResponse,
  GatedPromoRegisterPOSTRequest,
  GatedPromoRegisterPOSTSuccessResponse,
  GatedRegisterSessionPOSTSuccessResponse,
} from "ipctg-inflow-types/InFlowGatedAPI"
import { useState } from "react"
import { GridList, GridListItem } from "react-aria-components"
import { Controller, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import AeronauticStripe from "@/globalComponents/molecules/AeronauticStripe"
import TextField from "@/globalComponents/molecules/TextField"
import { apiCardBrandToIconName } from "@/utilities/etc"
import {
  GatedAutoPaySelectCardError,
  GatedLookupSessionsError,
  GatedPromoRegisterError,
  GatedRegisterSessionError,
} from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { sessionDetailsQueryOptions } from "@/utilities/hooks/queries/express"
import { setupIntentQueryOptions } from "@/utilities/hooks/queries/payment"
import {
  meQueryOptions,
  savedPaymentMethodsOptions,
} from "@/utilities/hooks/queries/user"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"
import { log } from "@/utilities/logging"

import PayingCashLauncher from "../../-components/PayingCash/PayingCashLauncher"

import { DiscountCodeSchema } from "./cart"
import styles from "./express.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/express"
)({
  staticData: {
    pageName: "Express Checkout",
  },
  component: RouteComponent,
  beforeLoad: async ({ context, params, location }) => {
    if (!context.isAuthenticated) {
      throw redirect({
        to: "/auth/sign-in",
        search: {
          redirect: location.href,
          qrLookupID: params.qrLookupID,
          phone: undefined,
          plate: undefined,
          conversionFlow: undefined,
          email: undefined,
        },
      })
    }
    context.queryClient.fetchQuery(meQueryOptions())
    context.queryClient.fetchQuery(savedPaymentMethodsOptions())
  },
})

const CUSTOM_PAYMENT_METHOD = "custom"

function RouteComponent() {
  const { t } = useTranslation(["gated", "welcome"])
  const { sessionID, qrLookupID } = Route.useParams()
  const { express } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { setError } = useErrorModal()
  const { scannedArea } = useTheme()
  const locationName = scannedArea?.theme.locationInformation.locationName
  const cashPaymentMode =
    scannedArea?.theme.metadata?.gated?.payment?.cash?.mode
  const { data: sessionDetails } = useSuspenseQuery(
    sessionDetailsQueryOptions({ sessionID })
  )
  const { data: savedPaymentMethodsRsp } = useSuspenseQuery(
    savedPaymentMethodsOptions()
  )
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const hasSavedPaymentMethods = !!savedPaymentMethodsRsp.paymentMethods.length

  const [paymentMethod, setPaymentMethod] = useState<Set<string>>(() => {
    const paymentMethodID = !hasSavedPaymentMethods
      ? CUSTOM_PAYMENT_METHOD
      : savedPaymentMethodsRsp.paymentMethods.find((pm) => pm.isDefault)
          ?.paymentMethodID ||
        savedPaymentMethodsRsp.paymentMethods[0].paymentMethodID
    return new Set([paymentMethodID])
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <>
      <Heading
        className="center"
        level={4}
        as={1}
        style={{ color: "var(--secondary-11)" }}
      >
        <Trans i18nKey="header" ns="welcome" values={{ locationName }}>
          Welcome to <span>{locationName}</span> Parking
        </Trans>
      </Heading>
      <Card>
        <div>
          <Heading level={1} as={2} className="center">
            {t("flowCard.subhead.hassleFreeParking", { ns: "welcome" })}
          </Heading>
          <AeronauticStripe />
        </div>
        <Text className="center">
          {/* TODO Fix me, we may not have rates external link, we should handle it a bit better */}
          <Trans
            i18nKey={
              hasSavedPaymentMethods
                ? "register.express.description"
                : "register.express.descriptionNoSavedPaymentMethod"
            }
            ns="gated"
            components={{
              1: (
                <a
                  className="body--1 semi-bold"
                  href={
                    scannedArea?.theme.drawer?.externalLinks?.find(
                      (el) => el.type === "rates"
                    )?.link || ""
                  }
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </Text>
        <VehicleCell plate={sessionDetails.session.cameraPlate} />
        {hasSavedPaymentMethods && (
          <Heading level={3} className="center" id="payment-section-header">
            {t("register.express.paymentSectionHeader", { ns: "gated" })}
          </Heading>
        )}
        <PromoCodeSection
          plate={sessionDetails.session.cameraPlate}
          hasSavedPaymentMethods={hasSavedPaymentMethods}
        />
        {hasSavedPaymentMethods && (
          <GridList
            aria-labelledby="payment-section-header"
            items={[
              ...savedPaymentMethodsRsp.paymentMethods.sort(
                (a, b) => Number(b.isDefault) - Number(a.isDefault)
              ),
              { type: "custom" },
            ]}
            selectionMode="single"
            selectionBehavior="toggle"
            dependencies={savedPaymentMethodsRsp.paymentMethods}
            selectedKeys={paymentMethod}
            onSelectionChange={(keys) => {
              // Shouldn't happen
              if (typeof keys === "string") {
                setPaymentMethod(
                  new Set([
                    savedPaymentMethodsRsp.paymentMethods[0].paymentMethodID,
                  ])
                )
              } else {
                const paymentMethodID = keys.values().next().value?.toString()
                setPaymentMethod(
                  paymentMethodID ? new Set([paymentMethodID]) : new Set()
                )
              }
            }}
            disallowEmptySelection
            className={styles.paymentMethodList}
          >
            {(paymentMethod) => {
              if ("paymentMethodID" in paymentMethod) {
                return (
                  <GridListItem
                    id={paymentMethod.paymentMethodID}
                    key={paymentMethod.paymentMethodID}
                    textValue={`${paymentMethod.cardBrand} •••• ${paymentMethod.last4}`}
                    className={styles.paymentMethod}
                  >
                    <div className="radio" data-hue="primary">
                      <div className="selectedIndicator" />
                    </div>
                    <div className={styles.brand}>
                      <Icon
                        name={apiCardBrandToIconName(paymentMethod)}
                        retainOriginalColors
                      />
                    </div>
                    <div>
                      <Text className="bold">
                        •••• {paymentMethod.last4}{" "}
                        {paymentMethod.isDefault && (
                          <span>
                            (
                            {t("register.express.defaultPaymentMethod", {
                              ns: "gated",
                            })}
                            )
                          </span>
                        )}
                      </Text>
                    </div>
                  </GridListItem>
                )
              } else {
                return (
                  <GridListItem
                    id={CUSTOM_PAYMENT_METHOD}
                    key={CUSTOM_PAYMENT_METHOD}
                    textValue={t("register.express.useDifferentPaymentMethod", {
                      ns: "gated",
                    })}
                    className={styles.paymentMethod}
                  >
                    <div className="radio" data-hue="primary">
                      <div className="selectedIndicator" />
                    </div>
                    <Text className="bold">
                      {t("register.express.useDifferentPaymentMethod", {
                        ns: "gated",
                      })}
                    </Text>
                  </GridListItem>
                )
              }
            }}
          </GridList>
        )}
        <Button
          buttonType="primary"
          isPending={isPending}
          onPress={() => mutate()}
        >
          {t("common.continue", { ns: "common" })}
        </Button>
        {cashPaymentMode === "visible" && (
          <PayingCashLauncher
            sessionID={sessionID}
            onSuccess={() =>
              navigate({
                to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
                search: { express: true },
              })
            }
          />
        )}
      </Card>
    </>
  )

  async function mutationFn() {
    const authSession = await fetchAuthSession()
    const cognitoIDToken = authSession.tokens?.idToken?.toString()
    const paymentMethodID = paymentMethod.values().next().value?.toString()

    if (paymentMethodID === CUSTOM_PAYMENT_METHOD) {
      const lookupSessionRsp = await api.gated.lookupSessions.POST({
        plate: sessionDetails.session.cameraPlate,
        method: "plate",
        bypassSessionsLookup: true,
      })

      if ("failReason" in lookupSessionRsp) {
        throw new GatedLookupSessionsError(lookupSessionRsp.failReason)
      }

      const rsp = await api.gated.registerSession.POST({
        sessionID,
        isReregistering: false,
        isInLane: false,
        phone: me?.cognitoUser.phone_number || null,
      })

      if ("failReason" in rsp) {
        throw new GatedRegisterSessionError(rsp.failReason)
      }

      return rsp
    }

    if (!cognitoIDToken) {
      throw new GatedAutoPaySelectCardError(
        GatedAutoPaySelectCardFailReason.Unauthorized
      )
    }

    if (!paymentMethodID) {
      throw new GatedAutoPaySelectCardError(
        GatedAutoPaySelectCardFailReason.PaymentMethodNotFound
      )
    }

    const rsp = await api.autoPaySelectCard.POST({
      cognitoIDToken,
      paymentMethodID,
      sessionID,
      qrLookupID,
    })

    if ("failReason" in rsp) {
      throw new GatedAutoPaySelectCardError(rsp.failReason)
    }

    return rsp
  }

  function onSuccess(
    rsp:
      | GatedRegisterSessionPOSTSuccessResponse
      | GatedAutoPaySelectCardPOSTSuccessResponse
  ) {
    if ("session" in rsp) {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/checkout",
        search: { express: true },
      })
    } else {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
        search: { key: rsp.confirmationKey, express },
      })
    }
  }

  function onError(error: Error) {
    log({
      level: "error",
      flow: "gated:register",
      scope: "payment.express",
      message: "Express checkout action failed",
      meta: { error },
    })
    setError({ error })
  }
}

function VehicleCell(props: { plate: string }) {
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const vehicle = me?.inflowUser.savedPlates.find(
    (v) => v.plate === props.plate
  )

  return (
    <div className={styles.detectedVehicle}>
      <div className={styles.iconContainer}>
        <Icon name="car" />
      </div>
      {vehicle?.nickname ? (
        <div>
          <Text className="semi-bold">{vehicle.nickname}</Text>
          <Text level={3}>{vehicle.plate}</Text>
        </div>
      ) : (
        <Text className="semi-bold">{props.plate}</Text>
      )}
    </div>
  )
}

function PromoCodeSection(props: {
  plate: string
  hasSavedPaymentMethods: boolean
}) {
  const { t } = useTranslation(["gated", "errors"])
  const { sessionID } = Route.useParams()
  const { express } = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { data: me, isPending: isMePending } = useQuery(meQueryOptions())

  const [showDiscountCodeField, toggleShowDiscountCodeField] = useToggle(false)
  const [discountCodeApplied, setDiscountCodeApplied] = useState("")
  const form = useForm<DiscountCodeSchema>({
    defaultValues: {
      promoCode: "",
    },
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  return (
    <>
      <Text
        level={2}
        className="center"
        style={{
          marginTop: props.hasSavedPaymentMethods
            ? "var(--space-2)"
            : undefined,
        }}
      >
        {!discountCodeApplied && (
          <Button
            buttonType="primary"
            presentation="link"
            onPress={() => {
              if (showDiscountCodeField) {
                setDiscountCodeApplied("")
              }
              toggleShowDiscountCodeField()
            }}
          >
            {showDiscountCodeField
              ? t("register.cart.promoCode.remove", { ns: "gated" })
              : t("register.cart.promoCode.add", { ns: "gated" })}
          </Button>
        )}
      </Text>
      {
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {showDiscountCodeField && !discountCodeApplied && (
            <fieldset disabled={isPending}>
              <Controller<DiscountCodeSchema>
                name="promoCode"
                control={form.control}
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                }}
                render={({
                  field: { ref, onChange, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    {...rest}
                    onChange={(v) => onChange(v.toUpperCase())}
                    isInvalid={invalid}
                    aria-label={t("register.cart.promoCode.fieldLabel", {
                      ns: "gated",
                    })}
                  >
                    <div className="promoCodeField">
                      <TextField.Input innerRef={ref} style={{ width: 0 }} />
                      <Button
                        type="submit"
                        buttonType="default"
                        isPending={isPending}
                        isDisabled={isMePending}
                        style={{ width: "fit-content", flexShrink: 0 }}
                      >
                        {t("register.cart.promoCode.apply", { ns: "gated" })}
                      </Button>
                    </div>
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </fieldset>
          )}
          {discountCodeApplied && (
            <Text level={2} className="highlighted-cell center">
              <b>{discountCodeApplied}</b>{" "}
              {t("register.cart.promoCode.successfullyApplied", {
                ns: "gated",
              })}
            </Text>
          )}
        </form>
      }
    </>
  )

  function onSubmit(data: DiscountCodeSchema) {
    mutate({ ...data, isRapidCheckout: true })
  }

  async function mutationFn(request: GatedPromoRegisterPOSTRequest) {
    const lookupSessionRsp = await api.gated.lookupSessions.POST({
      plate: props.plate,
      method: "plate",
      bypassSessionsLookup: true,
    })

    if ("failReason" in lookupSessionRsp) {
      throw new GatedLookupSessionsError(lookupSessionRsp.failReason)
    }

    const registerRsp = await api.gated.registerSession.POST({
      sessionID,
      isReregistering: false,
      isInLane: false,
      phone: me?.cognitoUser.phone_number || null,
    })

    if ("failReason" in registerRsp) {
      throw new GatedRegisterSessionError(registerRsp.failReason)
    }

    const rsp = await api.gated.promoRegister.POST(request)
    if ("failReason" in rsp) {
      const shouldThrow = [
        PromoFailReason.InvalidRequestFormat,
        PromoFailReason.SystemError,
        PromoFailReason.Unauthorized,
      ]
      const error = new GatedPromoRegisterError(rsp.failReason)
      if (shouldThrow.some((r) => r === rsp.failReason)) {
        throw error
      }

      form.setError("promoCode", {
        message: t(error.translationKey, { ns: "errors" }),
      })
      return
    }
    return rsp
  }

  function onSuccess(
    rsp: GatedPromoRegisterPOSTSuccessResponse | undefined,
    variables: GatedPromoRegisterPOSTRequest
  ) {
    if (!rsp) return
    if (rsp.bypassCC) {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
        search: { key: rsp.confirmationKey!, express },
      })
    } else {
      queryClient.removeQueries(setupIntentQueryOptions({ express: true }))
      setDiscountCodeApplied(variables.promoCode)
    }
  }
}
